import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Wrench,
  Calendar,
  PackageSearch,
  UserCheck,
  DollarSign,
  Plus,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface QuickAction {
  icon: React.ElementType;
  label: string;
  path: string;
  color: string;
}

const quickActions: QuickAction[] = [
  { icon: Wrench, label: "Jobs", path: "/specialist/jobs", color: "bg-primary" },
  { icon: Calendar, label: "Dashboard", path: "/specialist", color: "bg-secondary" },
  { icon: PackageSearch, label: "Inventory", path: "/specialist/inventory", color: "bg-accent" },
  { icon: UserCheck, label: "Profile", path: "/specialist/profile", color: "bg-success" },
  { icon: DollarSign, label: "Earnings", path: "/specialist/earnings", color: "bg-warning" },
];

export const QuickActionsMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  if (!isMobile) return null;

  const handleActionClick = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Quick Actions Menu */}
      <div className="fixed bottom-6 right-6 z-50">
        {/* Action Buttons */}
        <div
          className={cn(
            "flex flex-col-reverse gap-3 mb-3 transition-all duration-300",
            isOpen ? "opacity-100 scale-100" : "opacity-0 scale-0 pointer-events-none"
          )}
        >
          {quickActions.map((action) => {
            const Icon = action.icon;
            const isActive = location.pathname === action.path;
            return (
              <Button
                key={action.path}
                size="lg"
                className={cn(
                  "h-14 w-14 rounded-full shadow-lg transition-all",
                  isActive
                    ? "bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2"
                    : "bg-card text-card-foreground hover:bg-accent"
                )}
                onClick={() => handleActionClick(action.path)}
              >
                <Icon className="h-6 w-6" />
                <span className="sr-only">{action.label}</span>
              </Button>
            );
          })}
        </div>

        {/* Main FAB */}
        <Button
          size="lg"
          className={cn(
            "h-16 w-16 rounded-full shadow-lg transition-all",
            isOpen
              ? "bg-destructive hover:bg-destructive/90 rotate-45"
              : "bg-primary hover:bg-primary-hover rotate-0"
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? (
            <X className="h-7 w-7" />
          ) : (
            <Plus className="h-7 w-7" />
          )}
        </Button>
      </div>
    </>
  );
};
